/**
 * /prompts/:id/assistant — Prompt Assistant chat page (Story 8.5).
 *
 * Layout
 * ------
 *   ┌─────────────────────────────────────┬─────────────────────┐
 *   │  Chat panel                         │  ConversationSelector
 *   │  - history (ChatMessage[])          │  - filters
 *   │  - ProposalCard per assistant turn  │  - checkbox list
 *   │  - input + Send                     │  (sidebar, w-80)
 *   └─────────────────────────────────────┴─────────────────────┘
 *
 * RBAC (AC7): viewer is redirected to `/prompts/:id/assistant/history` —
 * read-only flows live there. Operator+ chats and applies; admin can also
 * activate the resulting version in the same call.
 *
 * After apply (AC4): navigate to `/prompts/:id/versions/:newVersionId` so
 * the operator lands on the version detail page with the new diff visible.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, History, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { ChatMessage } from '@/components/assistant/ChatMessage';
import {
  ConfirmModal,
  type ConfirmAction,
  type ValidationFailedReportLike,
} from '@/components/assistant/ConfirmModal';
import { ConversationSelector } from '@/components/assistant/ConversationSelector';
import { ProposalCard } from '@/components/assistant/ProposalCard';
import { useAuth } from '@/hooks/useAuth';
import {
  applySession,
  chatTurn,
  discardSession,
  previewSession,
  type ChatTurnResponse,
  type ProposedChange,
  type ProposedCodeChange,
} from '@/lib/promptAssistant';

interface UiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  proposedChanges?: ProposedChange[];
  proposedCodeChanges?: ProposedCodeChange[];
  tokensTotal?: number | null;
  latencyMs?: number | null;
  createdAt: string;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function PromptAssistantPage() {
  const { id: promptId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const role = user?.role ?? 'viewer';

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [userInput, setUserInput] = useState('');
  const [confirm, setConfirm] = useState<{
    action: ConfirmAction;
    preview?: string;
    validationFailedReport?: ValidationFailedReportLike | null;
  } | null>(null);

  const lastAssistantWithProposal = useMemo(
    () =>
      [...messages]
        .reverse()
        .find((m) => m.role === 'assistant' && (m.proposedChanges?.length ?? 0) > 0),
    [messages],
  );
  // Local state can't track remote 'applied'/'discarded' transitions reliably,
  // so the apply/discard mutations flip this flag on success → ProposalCard
  // hides the CTAs to avoid double-submits in the same browser tab.
  const [resolvedFlag, setResolvedFlag] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    // jsdom (test env) doesn't implement Element.prototype.scrollTo; guard
    // so render-time effects don't blow up unit tests.
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages.length]);

  // ── Mutations ──────────────────────────────────────────────────────────

  const chatMutation = useMutation({
    mutationFn: chatTurn,
    onSuccess: (response: ChatTurnResponse, variables) => {
      const now = new Date().toISOString();
      // The user message wasn't echoed by the API (it's persisted server-side).
      // Append both turns locally so the thread is rendered in order.
      setMessages((prev) => [
        ...prev,
        {
          id: `local-user-${prev.length}`,
          role: 'user',
          content: variables.userMessage,
          createdAt: now,
        },
        {
          id: response.messageId,
          role: 'assistant',
          content: response.analysis,
          proposedChanges: response.proposedChanges,
          proposedCodeChanges: response.proposedCodeChanges,
          tokensTotal: response.tokensTotal,
          latencyMs: response.latencyMs,
          createdAt: now,
        },
      ]);
      setSessionId(response.sessionId);
      setUserInput('');
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao consultar o assistente'));
    },
  });

  const previewMutation = useMutation({
    mutationFn: previewSession,
  });

  const applyMutation = useMutation({
    mutationFn: applySession,
    onSuccess: (response, variables) => {
      setResolvedFlag(true);
      setConfirm(null);
      // Story 8.5b — surface activation partial-fail with a warning toast so
      // the admin sees WHY the version was created but not activated.
      if (variables.activate && !response.activated) {
        toast.warning(`v${response.versionNumber} criada — ativação falhou`, {
          description:
            response.activationFailureReason ??
            'Motivo desconhecido. Tente ativar manualmente na página da versão.',
        });
      } else {
        const verb = response.activated ? 'aplicada e ativada' : 'aplicada';
        toast.success(`v${response.versionNumber} ${verb}.`);
      }
      // AC4: redirect to the version detail page (mesmo em activation
      // partial-fail — a versão existe e o operator pode ativar manualmente).
      navigate(`/prompts/${promptId}/versions/${response.versionId}`);
    },
    onError: (err) => {
      // Story 8.5b — when backend rejects with 422 VALIDATION_FAILED, surface
      // the report inline in the modal instead of fechar com toast.error
      // genérico. The operator stays in context and can iterate.
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as
          | { error?: { code?: string; report?: ValidationFailedReportLike } }
          | undefined;
        if (body?.error?.code === 'VALIDATION_FAILED' && body.error.report) {
          setConfirm((prev) =>
            prev ? { ...prev, validationFailedReport: body.error!.report ?? null } : prev,
          );
          return;
        }
      }
      toast.error(humanizeError(err, 'Falha ao aplicar proposta'));
    },
  });

  const discardMutation = useMutation({
    mutationFn: discardSession,
    onSuccess: () => {
      setResolvedFlag(true);
      setConfirm(null);
      toast.success('Proposta descartada.');
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao descartar proposta'));
    },
  });

  // ── Early returns ──────────────────────────────────────────────────────

  if (!promptId) {
    return <Navigate to="/prompts" replace />;
  }
  // AC7: viewers cannot start chat — redirect to history.
  if (role === 'viewer') {
    return <Navigate to={`/prompts/${promptId}/assistant/history`} replace />;
  }

  // ── Handlers ───────────────────────────────────────────────────────────

  function handleSend() {
    const trimmed = userInput.trim();
    if (trimmed.length === 0 || chatMutation.isPending) return;
    chatMutation.mutate({
      promptId: promptId!,
      sessionId,
      userMessage: trimmed,
      referenceConversationIds: selectedIds,
    });
  }

  function openConfirm(action: ConfirmAction) {
    if (!sessionId) return;
    if (action === 'discard') {
      setConfirm({ action });
      return;
    }
    // Apply paths: fetch preview first so the modal can show the merged prompt.
    setConfirm({ action });
    previewMutation.mutate(sessionId, {
      onSuccess: (preview) => {
        setConfirm({ action, preview: preview.proposedSystemPrompt });
      },
      onError: (err) => {
        toast.error(humanizeError(err, 'Falha ao gerar pré-visualização'));
        setConfirm(null);
      },
    });
  }

  function runConfirmedAction() {
    if (!sessionId || !confirm) return;
    if (confirm.action === 'discard') {
      discardMutation.mutate(sessionId);
    } else {
      applyMutation.mutate({
        sessionId,
        activate: confirm.action === 'apply-activate',
      });
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  const proposalForConfirm: ProposedChange[] = lastAssistantWithProposal?.proposedChanges ?? [];

  const pendingMutation =
    chatMutation.isPending ||
    applyMutation.isPending ||
    discardMutation.isPending ||
    previewMutation.isPending;

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/prompts/${promptId}`} aria-label="Voltar para detalhe do prompt">
              <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
            </Link>
          </Button>
          <h1 className="text-lg font-semibold">Assistente de prompt</h1>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={`/prompts/${promptId}/assistant/history`}>
            <History className="mr-1 h-4 w-4" /> Histórico
          </Link>
        </Button>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden md:grid-cols-[1fr_320px]">
        {/* Chat panel */}
        <Card className="flex flex-col overflow-hidden">
          <CardContent className="flex flex-1 flex-col gap-3 p-4">
            <div
              ref={scrollRef}
              className="flex-1 space-y-3 overflow-y-auto pr-1"
              data-testid="chat-history"
            >
              {messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                  <p>Descreva um problema no comportamento do agente em linguagem natural.</p>
                  <p className="text-xs">Exemplo: "está oferecendo o link cedo demais"</p>
                </div>
              ) : null}
              {messages.map((m) => (
                <div key={m.id} className="space-y-2">
                  <ChatMessage
                    role={m.role}
                    content={m.content}
                    createdAt={m.createdAt}
                    tokensTotal={m.tokensTotal}
                    latencyMs={m.latencyMs}
                  />
                  {m.role === 'assistant' && (m.proposedChanges?.length ?? 0) > 0 ? (
                    <ProposalCard
                      analysis=""
                      proposedChanges={m.proposedChanges ?? []}
                      proposedCodeChanges={m.proposedCodeChanges ?? []}
                      role={role}
                      pending={pendingMutation}
                      alreadyResolved={resolvedFlag}
                      onApply={(activate) => openConfirm(activate ? 'apply-activate' : 'apply')}
                      onDiscard={() => openConfirm('discard')}
                    />
                  ) : null}
                </div>
              ))}
              {chatMutation.isPending ? (
                <div className="text-xs italic text-muted-foreground">Aguardando resposta…</div>
              ) : null}
            </div>

            <div className="flex gap-2">
              <textarea
                aria-label="Mensagem para o assistente"
                data-testid="chat-input"
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                rows={2}
                placeholder="Descreva o problema…"
                value={userInput}
                disabled={chatMutation.isPending}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleSend}
                disabled={chatMutation.isPending || userInput.trim().length === 0}
                data-testid="chat-send"
              >
                <Send className="mr-1 h-4 w-4" /> Enviar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="overflow-hidden">
          <ConversationSelector
            selectedIds={selectedIds}
            onToggle={(id) =>
              setSelectedIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
              )
            }
          />
        </div>
      </div>

      {confirm ? (
        <ConfirmModal
          open={true}
          onOpenChange={(next) => {
            if (!next) setConfirm(null);
          }}
          action={confirm.action}
          proposedChanges={proposalForConfirm}
          proposedSystemPrompt={confirm.preview}
          validationFailedReport={confirm.validationFailedReport}
          pending={applyMutation.isPending || discardMutation.isPending}
          onConfirm={runConfirmedAction}
        />
      ) : null}
    </div>
  );
}
